const APP_STORE_BASE =
  'https://apps.apple.com/us/app/harbor-co-parent-together/id6756952606';

const PLAY_STORE_BASE =
  'https://play.google.com/store/apps/details?id=com.leakingsieve.harbor';

const APPLE_PROVIDER_TOKEN = '5SHUV2C56D';

/** UTM params we care about forwarding from the landing page to store links. */
const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

export interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string;
}

/**
 * Read UTM params and gclid from the current page URL.
 * Call once on page load and pass the result to store-link helpers.
 */
export function getUtmParamsFromUrl(): UtmParams {
  if (typeof window === 'undefined') return {};

  const params = new URLSearchParams(window.location.search);
  const result: UtmParams = {};

  for (const key of UTM_KEYS) {
    const val = params.get(key);
    if (val) {
      result[key] = val;
    }
  }

  const gclid = params.get('gclid');
  if (gclid) {
    result.gclid = gclid;
  }

  return result;
}

/**
 * Build a compact campaign token for Apple's `ct` param (max 64 chars).
 * Encodes the traffic source so it shows up in App Store Connect analytics.
 *
 * Format: {baseCampaign}-{source}-{medium}  (truncated to 64 chars)
 * Falls back to just baseCampaign if no UTM data present.
 */
function buildAppleCampaignToken(
  baseCampaign: string,
  utm: UtmParams
): string {
  const parts = [baseCampaign];

  if (utm.utm_source) parts.push(utm.utm_source);
  if (utm.utm_medium) parts.push(utm.utm_medium);
  if (utm.utm_campaign) parts.push(utm.utm_campaign);

  // Apple ct param max 64 chars
  return parts.join('-').substring(0, 64);
}

/**
 * Get an App Store URL with Apple campaign tracking params.
 * - pt: provider token (Apple developer team ID)
 * - ct: campaign token (up to 64 chars, shows in App Store Connect analytics)
 * - mt: media type (8 = mobile app)
 *
 * When UTM params are available from the landing page URL, they're encoded
 * into the campaign token for App Store Connect attribution.
 */
export function getAppStoreUrl(campaign: string, utm: UtmParams = {}): string {
  const ct = buildAppleCampaignToken(campaign, utm);
  return `${APP_STORE_BASE}?pt=${APPLE_PROVIDER_TOKEN}&ct=${encodeURIComponent(ct)}&mt=8`;
}

/**
 * Get a Play Store URL with UTM tracking params.
 * The `referrer` param is what Android's Install Referrer API reads on first launch.
 * URL-encode the entire referrer value so it survives the redirect.
 *
 * When UTM params are available from the landing page URL (e.g. from Google Ads),
 * they override the defaults so attribution flows end-to-end:
 *   Google Ad → website (gclid) → Play Store (utm_source=google) → app install referrer
 */
export function getPlayStoreUrl(campaign: string, utm: UtmParams = {}): string {
  const source = utm.utm_source || 'website';
  const medium = utm.utm_medium || 'web';
  const camp = utm.utm_campaign || campaign;

  let referrer = `utm_source=${encodeURIComponent(source)}&utm_medium=${encodeURIComponent(medium)}&utm_campaign=${encodeURIComponent(camp)}`;

  if (utm.utm_content) {
    referrer += `&utm_content=${encodeURIComponent(utm.utm_content)}`;
  }
  if (utm.utm_term) {
    referrer += `&utm_term=${encodeURIComponent(utm.utm_term)}`;
  }
  if (utm.gclid) {
    referrer += `&gclid=${encodeURIComponent(utm.gclid)}`;
  }

  return `${PLAY_STORE_BASE}&referrer=${encodeURIComponent(referrer)}`;
}
